/**
 * Per-browser pinned and recently used backoffice tools. Pure helpers (unit-tested) plus a small
 * localStorage wrapper. Stores locale-less tool paths only, so a locale switch keeps the same
 * pins, and unknown paths (a tool that was renamed or removed) are dropped when loading.
 */
export type BackofficeToolPrefs = { pinned: string[]; recent: string[] };

export const EMPTY_TOOL_PREFS: BackofficeToolPrefs = { pinned: [], recent: [] };
export const MAX_RECENT_TOOLS = 4;
export const TOOL_PREFS_STORAGE_KEY = 'jc.backoffice.toolPrefs.v1';
export const TOOL_PREFS_CHANGED_EVENT = 'jc:backoffice-tool-prefs-changed';

export function recordToolVisit(prefs: BackofficeToolPrefs, path: string): BackofficeToolPrefs {
  if (prefs.recent[0] === path) return prefs;
  return { ...prefs, recent: [path, ...prefs.recent.filter((p) => p !== path)].slice(0, MAX_RECENT_TOOLS) };
}

export function toggleToolPin(prefs: BackofficeToolPrefs, path: string): BackofficeToolPrefs {
  return prefs.pinned.includes(path)
    ? { ...prefs, pinned: prefs.pinned.filter((p) => p !== path) }
    : { ...prefs, pinned: [...prefs.pinned, path] };
}

/** Keep only known paths, dedupe, and coerce anything malformed to empty. */
export function sanitizeToolPrefs(raw: unknown, validPaths: ReadonlySet<string>): BackofficeToolPrefs {
  const clean = (value: unknown): string[] =>
    Array.isArray(value)
      ? [...new Set(value.filter((p): p is string => typeof p === 'string' && validPaths.has(p)))]
      : [];
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return { pinned: clean(source.pinned), recent: clean(source.recent).slice(0, MAX_RECENT_TOOLS) };
}

export function loadToolPrefs(validPaths: ReadonlySet<string>): BackofficeToolPrefs {
  try {
    const stored = window.localStorage.getItem(TOOL_PREFS_STORAGE_KEY);
    return sanitizeToolPrefs(stored ? JSON.parse(stored) : null, validPaths);
  } catch {
    return EMPTY_TOOL_PREFS;
  }
}

export function saveToolPrefs(prefs: BackofficeToolPrefs): void {
  try {
    window.localStorage.setItem(TOOL_PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Storage blocked or full: pins simply won't persist, nothing else breaks.
  }
  window.dispatchEvent(new Event(TOOL_PREFS_CHANGED_EVENT));
}
